import { memo, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { Text, Popover, Icon, useTheme } from '@Sage/hoi-poi-ui';
import { useDnD } from '@Sage/ssm-web5';

import { getLiteral } from 'utils/getLiteral';

import DisabledEntity from './DisabledEntity';
import SortableEntity from './SortableEntity';

const reorderEntities = (list, fromIndex, toIndex) => {
    const result = [...list];
    const [removed] = result.splice(fromIndex, 1);
    result.splice(toIndex, 0, removed);
    return result;
};

const mergeSortableItemProps = (itemProps, handleProps) => {
    const { style: itemStyle, ...restItemProps } = itemProps;
    const { style: handleStyle, ...restHandleProps } = handleProps;
    const stableItemStyle = { ...itemStyle };
    delete stableItemStyle.willChange;
    delete stableItemStyle.marginBottom;

    return {
        ...restItemProps,
        ...restHandleProps,
        style: {
            ...stableItemStyle,
            ...handleStyle,
            marginBottom: 0,
            ...(stableItemStyle.transform ? { willChange: 'transform' } : {}),
        },
    };
};

const ScheduleSortingMapEntities = ({ entities, disabledEntities, handleDragEnd }) => {
    const theme = useTheme();
    const [localEntities, setLocalEntities] = useState(entities);

    useEffect(() => {
        setLocalEntities(entities);
    }, [entities]);

    const onReorder = useCallback(
        (fromIndex, toIndex) => {
            setLocalEntities((prev) => {
                const reordered = reorderEntities(prev, fromIndex, toIndex);
                handleDragEnd(reordered);
                return reordered;
            });
        },
        [handleDragEnd],
    );

    const { containerProps, getDragHandleProps, getItemProps, isDragging } = useDnD({
        items: localEntities,
        onReorder,
    });

    return (
        <div {...containerProps} className="fm-schedule-sorting-map-modal__entities">
            <div
                className={classnames('fm-schedule-sorting-map-modal__sortable-list', {
                    'fm-schedule-sorting-map-modal__sortable-list--dragging': isDragging,
                })}
            >
                {localEntities.map((entity, index) => (
                    <div
                        key={entity.AccountId}
                        {...mergeSortableItemProps(getItemProps(index), getDragHandleProps(index))}
                        className="fm-schedule-sorting-map-modal__sortable-item"
                    >
                        <SortableEntity entity={entity} index={index} />
                    </div>
                ))}
            </div>
            {!!disabledEntities?.length && (
                <div className="fm-schedule-sorting-map-modal__disabled-entities">
                    <Text
                        type="subtitle"
                        color={theme.colors.utility.textSecondary}
                        className="fm-schedule-sorting-map-modal__disableds-title"
                    >
                        {getLiteral('label_accounts_not_showing')}
                        <Popover
                            placement="top"
                            content={getLiteral('label_hover_accounts_not_geolocalized')}
                        >
                            <Icon name="helpOutline" />
                        </Popover>
                    </Text>
                    {disabledEntities.map((entity, index) => (
                        <DisabledEntity
                            key={entity.AccountId}
                            startIndex={localEntities.length}
                            index={index}
                            entity={entity}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default memo(ScheduleSortingMapEntities);
